import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';
import useAutoRefresh from './hooks/useAutoRefresh';
import { format, addDays, subDays, startOfWeek, endOfWeek, isToday, isSameDay, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';

function BookingCalendar() {
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('list'); // 'day' | 'week' | 'month' | 'list'
  const [listFilter, setListFilter] = useState('upcoming'); // 'upcoming' | 'past' | 'all'

  const [newBooking, setNewBooking] = useState({
    contact_id: '',
    service_id: '',
    staff_id: '',
    booking_date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '10:00',
    notes: ''
  });
  const [addingNewCustomer, setAddingNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ first_name: "", last_name: "", phone: "", email: "" });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (silent) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      const [bookingsRes, servicesRes, staffRes, customersRes] = await Promise.all([
        axios.get('/api/bookings'),
        axios.get('/api/services?active_only=true'),
        axios.get('/api/staff'),
        axios.get('/api/customers')
      ]);

      var rawBookings = bookingsRes.data.bookings || [];
      // Normalize booking_date to 'YYYY-MM-DD' (Supabase sometimes returns full timestamp)
      rawBookings.forEach(function(b) {
        if (b.booking_date && b.booking_date.length > 10) {
          b.booking_date = b.booking_date.slice(0, 10);
        }
      });
      setBookings(rawBookings);
      setServices(servicesRes.data.services || []);
      setStaff(staffRes.data.staff || []);
      setCustomers(customersRes.data.customers || []);
      setLoading(false);
    } catch (err) {
      console.error('Error loading data:', err);
      if (!silent) setError('Failed to connect to server');
      setLoading(false);
    }
  };

  useAutoRefresh(function() { loadData(true); }, 15000);

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    
    if (!newBooking.contact_id || !newBooking.service_id || !newBooking.staff_id) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const response = await axios.post('/api/bookings', {
        contact_id: parseInt(newBooking.contact_id),
        service_id: parseInt(newBooking.service_id),
        staff_id: parseInt(newBooking.staff_id),
        booking_date: newBooking.booking_date,
        start_time: newBooking.start_time,
        notes: newBooking.notes || ''
      });
      
      if (response.data.success) {
        alert('✅ Booking created successfully!');
        setShowNewBooking(false);
        loadData();
        setNewBooking({
          contact_id: '',
          service_id: '',
          staff_id: '',
          booking_date: format(new Date(), 'yyyy-MM-dd'),
          start_time: '10:00',
          notes: ''
        });
      }
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleComplete = async (id) => {
    try {
      await axios.put(`/api/bookings/${id}/status`, { status: 'completed' });
      loadData();
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCheckIn = async (id) => {
    try {
      const res = await axios.put(`/api/bookings/${id}/status`, { status: 'checked_in' });
      if (res.data.work_order) {
        alert('✅ Checked in! Work order ' + res.data.work_order.order_number + ' created and tracking link sent to customer.');
      } else {
        alert('✅ Checked in!');
      }
      loadData();
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleNoShow = async (id) => {
    try {
      await axios.put(`/api/bookings/${id}/status`, { status: 'no-show' });
      loadData();
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancelBooking = async (id) => {
    if (!window.confirm('Cancel this booking?')) return;
    try {
      await axios.put(`/api/bookings/${id}/status`, { status: 'cancelled' });
      loadData();
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`/api/bookings/${id}/approve`);
      loadData();
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDecline = async (id) => {
    var reason = window.prompt('Reason for declining (optional):');
    if (reason === null) return; // cancelled prompt
    try {
      await axios.put(`/api/bookings/${id}/decline`, { reason });
      loadData();
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.error || err.message));
    }
  };

  // ============================================================================
  // DATE HELPERS
  // ============================================================================
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
  const weekDays = [...Array(7)].map((_, i) => addDays(weekStart, i));

  const todayBookings = bookings.filter(b =>
    (b.booking_date || '').slice(0, 10) === format(selectedDate, 'yyyy-MM-dd')
  );

  const goToday = () => setSelectedDate(new Date());
  const goPrevDay = () => setSelectedDate(prev => subDays(prev, 1));
  const goNextDay = () => setSelectedDate(prev => addDays(prev, 1));
  const goPrevWeek = () => setSelectedDate(prev => subDays(prev, 7));
  const goNextWeek = () => setSelectedDate(prev => addDays(prev, 7));
  const goPrevMonth = () => setSelectedDate(prev => subMonths(prev, 1));
  const goNextMonth = () => setSelectedDate(prev => addMonths(prev, 1));

  // List view bookings
  const getListBookings = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    let filtered = [...bookings];
    if (listFilter === 'upcoming') {
      filtered = filtered.filter(b => b.booking_date >= today && b.status !== 'cancelled');
    } else if (listFilter === 'past') {
      filtered = filtered.filter(b => b.booking_date < today || b.status === 'completed');
    }
    return filtered.sort((a, b) => {
      const dateComp = a.booking_date.localeCompare(b.booking_date);
      if (dateComp !== 0) return listFilter === 'past' ? -dateComp : dateComp;
      return (a.start_time || '').localeCompare(b.start_time || '');
    });
  };

  // Month calendar helpers
  const monthStart = startOfMonth(selectedDate);
  const monthEnd = endOfMonth(selectedDate);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPadding = getDay(monthStart); // 0=Sun

  // ============================================================================
  // RENDER
  // ============================================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-2xl text-slate-400">Loading bookings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="bg-surface p-8 rounded-lg border border-red-700 max-w-md">
          <h2 className="text-xl font-bold text-red-500 mb-4">⚠️ Connection Error</h2>
          <p className="text-slate-300 mb-4">{error}</p>
          <button onClick={loadData}
            className="w-full bg-primary hover:bg-purple-600 text-white py-2 rounded-lg font-semibold">
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      {/* HEADER */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">📅 Bookings</h1>
          <p className="text-slate-400">{bookings.length} total bookings</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex bg-surface border border-slate-700 rounded-lg overflow-hidden">
            {['day', 'week', 'month', 'list'].map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`px-4 py-2 text-sm font-semibold transition-all ${
                  view === v ? 'bg-primary text-white' : 'text-slate-400 hover:text-white'
                }`}>
                {v === 'day' ? 'Day' : v === 'week' ? 'Week' : v === 'month' ? 'Month' : 'List'}
              </button>
            ))}
          </div>
          <button onClick={() => setShowNewBooking(true)}
            className="bg-primary hover:bg-purple-600 text-white px-6 py-2.5 rounded-lg font-semibold shadow-lg hover:scale-105 transition-all">
            + New Booking
          </button>
        </div>
      </div>

      {/* PENDING REQUESTS BANNER */}
      {bookings.filter(b => b.status === 'pending').length > 0 && (
        <div className="mb-4 p-4 rounded-xl border border-orange-700/50 bg-orange-950/30" style={{ animation: 'pulse 2s infinite' }}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔔</span>
              <div>
                <div className="text-orange-300 font-bold text-sm">
                  {bookings.filter(b => b.status === 'pending').length} Pending Request{bookings.filter(b => b.status === 'pending').length > 1 ? 's' : ''}
                </div>
                <div className="text-orange-400/70 text-xs">New online bookings waiting for approval</div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {bookings.filter(b => b.status === 'pending').slice(0, 5).map(b => {
                var customer = b.contacts || {};
                var service = b.services || {};
                var dateStr = b.booking_date ? new Date(b.booking_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                return (
                  <div key={b.id} className="bg-orange-900/40 rounded-lg px-3 py-2 flex items-center gap-3 border border-orange-800/40">
                    <div>
                      <div className="text-white text-xs font-semibold">{customer.first_name} {customer.last_name}</div>
                      <div className="text-orange-400/70 text-xs">{service.name} · {dateStr} {b.start_time ? b.start_time.slice(0,5) : ''}</div>
                    </div>
                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                      <button onClick={() => handleApprove(b.id)}
                        className="px-2 py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-bold">✓</button>
                      <button onClick={() => handleDecline(b.id)}
                        className="px-2 py-1 rounded bg-red-700 hover:bg-red-600 text-white text-xs font-bold">✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ================================================================== */}
      {/* DAY VIEW */}
      {/* ================================================================== */}
      {view === 'day' && (
        <>
          {/* Day Navigation */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <button onClick={goPrevDay}
                className="bg-surface border border-slate-700 text-slate-300 hover:text-white hover:border-primary px-3 py-2 rounded-lg transition-all text-lg">
                ←
              </button>
              <button onClick={goToday}
                className="bg-surface border border-slate-700 text-slate-300 hover:text-white hover:border-primary px-4 py-2 rounded-lg transition-all text-sm font-semibold">
                Today
              </button>
              <button onClick={goNextDay}
                className="bg-surface border border-slate-700 text-slate-300 hover:text-white hover:border-primary px-3 py-2 rounded-lg transition-all text-lg">
                →
              </button>
            </div>
            <div className="text-white font-semibold text-lg">
              {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              {isToday(selectedDate) && <span className="ml-2 text-xs bg-emerald-600 text-white px-2 py-0.5 rounded-full">Today</span>}
            </div>
            <div className="text-slate-400 text-sm">
              {todayBookings.length} booking{todayBookings.length !== 1 ? 's' : ''}
              {todayBookings.filter(b => b.status === 'pending').length > 0 && (
                <span className="ml-2 text-orange-400 font-semibold">
                  ({todayBookings.filter(b => b.status === 'pending').length} pending)
                </span>
              )}
            </div>
          </div>

          {/* Hourly Timeline */}
          <div className="bg-surface border border-slate-700 rounded-xl overflow-hidden">
            {/* Unscheduled bookings (no start_time) */}
            {todayBookings.filter(b => !b.start_time).length > 0 && (
              <div className="flex border-b border-slate-700/50 bg-slate-800/50">
                <div className="w-20 flex-shrink-0 py-3 px-3 text-right text-xs font-mono border-r border-slate-700/50 text-orange-400">
                  No time
                </div>
                <div className="flex-1 py-2 px-3">
                  {todayBookings.filter(b => !b.start_time).map(booking => {
                    var customer = booking.contacts || {};
                    var service = booking.services || {};
                    return (
                      <div key={booking.id} className="rounded-lg border-l-4 border-orange-500 bg-orange-950/40 px-4 py-3 mb-2">
                        <div className="text-white font-semibold text-sm">{customer.first_name} {customer.last_name}</div>
                        <div className="text-slate-400 text-xs">{service.name || '—'}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {Array.from({ length: 16 }, (_, i) => i + 6).map(hour => {
              var hourStr = String(hour).padStart(2, '0');
              var hourBookings = todayBookings.filter(b => {
                if (!b.start_time) return false;
                var bHour = parseInt(b.start_time.split(':')[0]);
                return bHour === hour;
              });
              var timeLabel = hour === 0 ? '12 AM' : hour < 12 ? hour + ' AM' : hour === 12 ? '12 PM' : (hour - 12) + ' PM';
              var isNowHour = isToday(selectedDate) && new Date().getHours() === hour;

              return (
                <div key={hour} className={'flex border-b border-slate-700/50' + (isNowHour ? ' bg-primary/5' : '')}>
                  {/* Time gutter */}
                  <div className={'w-20 flex-shrink-0 py-3 px-3 text-right text-xs font-mono border-r border-slate-700/50' + (isNowHour ? ' text-primary font-bold' : ' text-slate-500')}>
                    {timeLabel}
                    {isNowHour && <div className="text-primary text-[10px]">● NOW</div>}
                  </div>

                  {/* Booking slots */}
                  <div className="flex-1 py-2 px-3 min-h-[56px]">
                    {hourBookings.length === 0 && (
                      <div className="h-full flex items-center">
                        <div className="w-full border border-dashed border-slate-700/30 rounded h-1"></div>
                      </div>
                    )}
                    {hourBookings.map(booking => {
                      var customer = booking.contacts || {};
                      var service = booking.services || {};
                      var staff = booking.staff || {};
                      var isPending = booking.status === 'pending';
                      var statusColors = {
                        pending: 'border-orange-500 bg-orange-950/40',
                        confirmed: 'border-blue-500 bg-blue-950/30',
                        checked_in: 'border-amber-500 bg-amber-950/30',
                        completed: 'border-emerald-500 bg-emerald-950/30',
                        cancelled: 'border-slate-600 bg-slate-800/30 opacity-50',
                        'no-show': 'border-red-500 bg-red-950/30 opacity-60',
                      };

                      return (
                        <div key={booking.id}
                          onClick={() => { if (booking.booking_date) { setSelectedDate(new Date(booking.booking_date + 'T12:00:00')); } }}
                          className={'rounded-lg border-l-4 px-4 py-3 mb-2 cursor-pointer hover:brightness-110 transition-all ' + (statusColors[booking.status] || 'border-slate-500 bg-slate-800/30')}>
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="text-primary font-mono text-sm font-bold flex-shrink-0">
                                {booking.start_time ? booking.start_time.slice(0, 5) : '—'}
                              </div>
                              <div className="min-w-0">
                                <div className="text-white font-semibold text-sm truncate">
                                  {customer.first_name} {customer.last_name}
                                </div>
                                <div className="text-slate-400 text-xs truncate">
                                  {service.name || '—'} · {service.duration || '—'} min
                                  {staff.first_name ? ' · ' + staff.first_name : ''}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-emerald-400 font-bold text-sm">
                                {booking.final_price ? '$' + booking.final_price : service.price ? '$' + service.price : ''}
                              </span>
                              <span className={'inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ' + (
                                booking.status === 'pending' ? 'bg-orange-900 text-orange-300' :
                                booking.status === 'confirmed' ? 'bg-blue-900 text-blue-300' :
                                booking.status === 'checked_in' ? 'bg-amber-900 text-amber-300' :
                                booking.status === 'completed' ? 'bg-emerald-900 text-emerald-300' :
                                booking.status === 'cancelled' ? 'bg-slate-700 text-slate-400' :
                                'bg-red-900 text-red-300'
                              )}>
                                {booking.status === 'pending' ? '⏳ PENDING' : booking.status === 'checked_in' ? 'CHECKED IN' : booking.status === 'no-show' ? 'NO-SHOW' : (booking.status || '').toUpperCase()}
                              </span>
                              {/* Inline actions */}
                              {isPending && (
                                <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                                  <button onClick={() => handleApprove(booking.id)}
                                    className="px-2 py-0.5 rounded bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-bold">✓</button>
                                  <button onClick={() => handleDecline(booking.id)}
                                    className="px-2 py-0.5 rounded bg-red-700 hover:bg-red-600 text-white text-xs font-bold">✕</button>
                                </div>
                              )}
                              {booking.status === 'confirmed' && (
                                <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                                  <button onClick={() => handleCheckIn(booking.id)}
                                    className="text-amber-400 hover:text-amber-300 text-xs" title="Check In">🏁</button>
                                  <button onClick={() => handleComplete(booking.id)}
                                    className="text-emerald-400 hover:text-emerald-300 text-xs" title="Complete">✅</button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ================================================================== */}
      {/* WEEK VIEW */}
      {/* ================================================================== */}
      {view === 'week' && (
        <>
          {/* Week Navigation */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <button onClick={goPrevWeek}
                className="bg-surface border border-slate-700 text-slate-300 hover:text-white hover:border-primary px-3 py-2 rounded-lg transition-all text-lg">
                ←
              </button>
              <button onClick={goToday}
                className="bg-surface border border-slate-700 text-slate-300 hover:text-white hover:border-primary px-4 py-2 rounded-lg transition-all text-sm font-semibold">
                Today
              </button>
              <button onClick={goNextWeek}
                className="bg-surface border border-slate-700 text-slate-300 hover:text-white hover:border-primary px-3 py-2 rounded-lg transition-all text-lg">
                →
              </button>
            </div>
            <div className="text-white font-semibold text-lg">
              {format(weekStart, 'MMM d')} – {format(addDays(weekStart, 6), 'MMM d, yyyy')}
            </div>
          </div>

          {/* Day Buttons */}
          <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
            {weekDays.map((date, i) => {
              const isSelected = isSameDay(date, selectedDate);
              const dayBookings = bookings.filter(b => (b.booking_date || '').slice(0, 10) === format(date, 'yyyy-MM-dd'));
              const today = isToday(date);

              return (
                <button key={i} onClick={() => setSelectedDate(date)}
                  className={`flex-shrink-0 p-4 rounded-lg border transition-all min-w-[80px] ${
                    isSelected
                      ? 'bg-primary border-primary text-white shadow-lg scale-105'
                      : today
                        ? 'bg-surface border-emerald-600 text-emerald-300 hover:border-primary'
                        : 'bg-surface border-slate-700 text-slate-300 hover:border-primary'
                  }`}>
                  <div className="text-sm">{format(date, 'EEE')}</div>
                  <div className="text-2xl font-bold">{format(date, 'd')}</div>
                  {dayBookings.length > 0 && (
                    <div className={`text-xs mt-1 rounded-full px-2 py-0.5 ${
                      isSelected ? 'bg-white/20' : 'bg-emerald-600'
                    }`}>
                      {dayBookings.length}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Day Schedule */}
          <div>
            <h2 className="text-xl font-bold text-white mb-4">
              {format(selectedDate, 'EEEE, MMMM d')} — {todayBookings.length} booking{todayBookings.length !== 1 ? 's' : ''}
            </h2>
            {renderBookingCards(todayBookings)}
          </div>
        </>
      )}

      {/* ================================================================== */}
      {/* MONTH VIEW */}
      {/* ================================================================== */}
      {view === 'month' && (
        <>
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <button onClick={goPrevMonth}
                className="bg-surface border border-slate-700 text-slate-300 hover:text-white hover:border-primary px-3 py-2 rounded-lg transition-all text-lg">
                ←
              </button>
              <button onClick={goToday}
                className="bg-surface border border-slate-700 text-slate-300 hover:text-white hover:border-primary px-4 py-2 rounded-lg transition-all text-sm font-semibold">
                Today
              </button>
              <button onClick={goNextMonth}
                className="bg-surface border border-slate-700 text-slate-300 hover:text-white hover:border-primary px-3 py-2 rounded-lg transition-all text-lg">
                →
              </button>
            </div>
            <div className="text-white font-semibold text-xl">
              {format(selectedDate, 'MMMM yyyy')}
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="bg-surface border border-slate-700 rounded-lg overflow-hidden mb-6">
            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b border-slate-700">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="text-center text-slate-400 text-sm font-semibold py-3">
                  {d}
                </div>
              ))}
            </div>

            {/* Day Cells */}
            <div className="grid grid-cols-7">
              {/* Empty cells for padding */}
              {[...Array(startPadding)].map((_, i) => (
                <div key={`pad-${i}`} className="min-h-[80px] border-b border-r border-slate-700/50 bg-slate-900/30"></div>
              ))}
              {monthDays.map(date => {
                const dayStr = format(date, 'yyyy-MM-dd');
                const dayBookings = bookings.filter(b => (b.booking_date || '').slice(0, 10) === dayStr);
                const isSelected = isSameDay(date, selectedDate);
                const today = isToday(date);

                return (
                  <div key={dayStr}
                    onClick={() => { setSelectedDate(date); setView('day'); }}
                    className={`min-h-[80px] border-b border-r border-slate-700/50 p-2 cursor-pointer transition-all hover:bg-slate-700/30 ${
                      isSelected ? 'bg-primary/20 ring-1 ring-primary' : ''
                    }`}>
                    <div className={`text-sm font-semibold mb-1 ${
                      today ? 'text-emerald-400' : isSelected ? 'text-primary' : 'text-slate-300'
                    }`}>
                      {format(date, 'd')}
                    </div>
                    {dayBookings.length > 0 && (
                      <div className="text-xs bg-primary/30 text-primary rounded px-1.5 py-0.5 inline-block font-semibold">
                        {dayBookings.length} appt{dayBookings.length !== 1 ? 's' : ''}
                      </div>
                    )}
                    {dayBookings.slice(0, 2).map(b => (
                      <div key={b.id} className="text-xs text-slate-400 truncate mt-0.5">
                        {b.start_time} {b.contacts?.first_name || ''}
                      </div>
                    ))}
                    {dayBookings.length > 2 && (
                      <div className="text-xs text-slate-500">+{dayBookings.length - 2} more</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selected Day Detail */}
          <div>
            <h2 className="text-xl font-bold text-white mb-4">
              {format(selectedDate, 'EEEE, MMMM d')} — {todayBookings.length} booking{todayBookings.length !== 1 ? 's' : ''}
            </h2>
            {renderBookingCards(todayBookings)}
          </div>
        </>
      )}

      {/* ================================================================== */}
      {/* LIST VIEW */}
      {/* ================================================================== */}
      {view === 'list' && (
        <>
          {/* Filter Tabs */}
          <div className="flex gap-2 mb-6">
            {[
              { key: 'upcoming', label: 'Upcoming' },
              { key: 'past', label: 'Past' },
              { key: 'all', label: 'All' },
            ].map(f => (
              <button key={f.key} onClick={() => setListFilter(f.key)}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                  listFilter === f.key
                    ? 'bg-primary text-white'
                    : 'bg-surface border border-slate-700 text-slate-400 hover:text-white hover:border-primary'
                }`}>
                {f.label}
              </button>
            ))}
          </div>

          {/* List Table */}
          <div className="bg-surface border border-slate-700 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-slate-700 text-xs font-semibold text-slate-400 uppercase">
              <div className="col-span-2">Date</div>
              <div className="col-span-1">Time</div>
              <div className="col-span-3">Customer</div>
              <div className="col-span-2">Service</div>
              <div className="col-span-1">Staff</div>
              <div className="col-span-1">Price</div>
              <div className="col-span-2">Status</div>
            </div>

            {/* Rows */}
            {getListBookings().length === 0 && (
              <div className="p-12 text-center text-slate-500">
                <div className="text-4xl mb-3">📋</div>
                No bookings found
              </div>
            )}
            {getListBookings().map(booking => {
              const service = booking.services || services.find(s => s.id === booking.service_id);
              const staffMember = booking.staff || staff.find(s => s.id === booking.staff_id);
              const customer = booking.contacts || customers.find(c => c.id === booking.contact_id);
              const bookingDate = booking.booking_date ? parseISO(booking.booking_date) : null;
              const isPast = bookingDate && bookingDate < new Date() && booking.status !== 'completed';

              return (
                <div key={booking.id}
                  className={`grid grid-cols-12 gap-2 px-4 py-3 border-b border-slate-700/50 items-center hover:bg-slate-700/20 transition-all cursor-pointer ${
                    isPast && booking.status === 'confirmed' ? 'opacity-60' : ''
                  }`}
                  onClick={() => { if (bookingDate) { setSelectedDate(bookingDate); setView('day'); } }}>
                  <div className="col-span-2 text-white font-semibold text-sm">
                    {bookingDate ? format(bookingDate, 'MMM d, yyyy') : '—'}
                    {bookingDate && isToday(bookingDate) && (
                      <span className="ml-2 text-xs text-emerald-400 font-semibold">TODAY</span>
                    )}
                  </div>
                  <div className="col-span-1 text-primary font-bold text-sm">
                    {booking.start_time || '—'}
                  </div>
                  <div className="col-span-3">
                    <div className="text-white text-sm font-semibold">
                      {customer?.first_name} {customer?.last_name}
                    </div>
                    {customer?.phone && <div className="text-slate-500 text-xs">{customer.phone}</div>}
                  </div>
                  <div className="col-span-2">
                    <div className="text-slate-300 text-sm">{service?.name || '—'}</div>
                    {service?.duration && <div className="text-slate-500 text-xs">{service.duration} min</div>}
                  </div>
                  <div className="col-span-1 text-slate-400 text-sm">
                    {staffMember?.first_name || '—'}
                  </div>
                  <div className="col-span-1 text-emerald-400 font-bold text-sm">
                    {booking.final_price ? `$${booking.final_price}` : service?.price ? `$${service.price}` : '—'}
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                      booking.status === 'completed' ? 'bg-emerald-900 text-emerald-300' :
                      booking.status === 'confirmed' ? 'bg-blue-900 text-blue-300' :
                      booking.status === 'checked_in' ? 'bg-amber-900 text-amber-300' :
                      booking.status === 'no-show' ? 'bg-red-900 text-red-300' :
                      booking.status === 'cancelled' ? 'bg-slate-700 text-slate-400' :
                      booking.status === 'pending' ? 'bg-orange-900 text-orange-300' :
                      'bg-slate-700 text-slate-300'
                    }`}>
                      {booking.status === 'no-show' ? 'NO-SHOW' : booking.status === 'checked_in' ? 'CHECKED IN' : booking.status === 'pending' ? '⏳ PENDING' : (booking.status || 'pending').toUpperCase()}
                    </span>
                    {/* Approve/Decline for pending bookings */}
                    {booking.status === 'pending' && (
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <button onClick={() => handleApprove(booking.id)}
                          className="px-2 py-0.5 rounded bg-emerald-900 text-emerald-300 hover:bg-emerald-800 text-xs font-semibold" title="Approve">✓ Approve</button>
                        <button onClick={() => handleDecline(booking.id)}
                          className="px-2 py-0.5 rounded bg-red-900 text-red-300 hover:bg-red-800 text-xs font-semibold" title="Decline">✕ Decline</button>
                      </div>
                    )}
                    {/* Quick actions for confirmed bookings */}
                    {booking.status === 'confirmed' && (
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <button onClick={() => handleCheckIn(booking.id)}
                          className="text-amber-400 hover:text-amber-300 text-xs" title="Check In">🏁</button>
                        <button onClick={() => handleComplete(booking.id)}
                          className="text-emerald-400 hover:text-emerald-300 text-xs" title="Complete">✅</button>
                        <button onClick={() => handleNoShow(booking.id)}
                          className="text-red-400 hover:text-red-300 text-xs" title="No-Show">⚠️</button>
                        <button onClick={() => handleCancelBooking(booking.id)}
                          className="text-slate-400 hover:text-slate-300 text-xs" title="Cancel">❌</button>
                      </div>
                    )}
                    {booking.status === 'checked_in' && (
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <span className="text-amber-400 text-xs">🏁</span>
                        <button onClick={() => handleComplete(booking.id)}
                          className="text-emerald-400 hover:text-emerald-300 text-xs" title="Complete">✅</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="mt-4 flex gap-4 text-sm text-slate-400">
            <span>Showing {getListBookings().length} bookings</span>
            <span>•</span>
            <span className="text-emerald-400">
              ${getListBookings().reduce((sum, b) => sum + (parseFloat(b.final_price) || 0), 0).toFixed(2)} revenue
            </span>
          </div>
        </>
      )}

      {/* ================================================================== */}
      {/* NEW BOOKING MODAL (unchanged) */}
      {/* ================================================================== */}
      {showNewBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-lg max-w-2xl w-full p-6 max-h-screen overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">📅 New Booking</h2>
              <button onClick={() => setShowNewBooking(false)}
                className="text-slate-400 hover:text-white text-3xl leading-none">×</button>
            </div>

            <form onSubmit={handleCreateBooking} className="space-y-4">
              <div>
                <label className="block text-slate-300 mb-2 font-semibold">Customer *</label>
                {!addingNewCustomer ? (
                  <select value={newBooking.contact_id}
                    onChange={(e) => {
                      if (e.target.value === '__new__') {
                        setAddingNewCustomer(true);
                        setNewBooking({...newBooking, contact_id: ''});
                      } else {
                        setNewBooking({...newBooking, contact_id: e.target.value});
                      }
                    }}
                    className="w-full bg-background border border-slate-700 text-white p-3 rounded-lg focus:border-primary focus:outline-none"
                    required={!addingNewCustomer}>
                    <option value="">Select customer...</option>
                    <option value="__new__">➕ Add New Customer</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.first_name} {c.last_name} - {c.phone}</option>
                    ))}
                  </select>
                ) : (
                  <div className="space-y-3 p-4 border border-slate-600 rounded-lg bg-slate-800/50">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-primary font-semibold">New Customer</span>
                      <button type="button" onClick={() => { setAddingNewCustomer(false); setNewCustomer({ first_name: '', last_name: '', phone: '', email: '' }); }}
                        className="text-slate-400 hover:text-white text-sm">✕ Cancel</button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input placeholder="First Name *" value={newCustomer.first_name}
                        onChange={(e) => setNewCustomer({...newCustomer, first_name: e.target.value})}
                        className="bg-background border border-slate-700 text-white p-2.5 rounded-lg focus:border-primary focus:outline-none text-sm" required />
                      <input placeholder="Last Name" value={newCustomer.last_name}
                        onChange={(e) => setNewCustomer({...newCustomer, last_name: e.target.value})}
                        className="bg-background border border-slate-700 text-white p-2.5 rounded-lg focus:border-primary focus:outline-none text-sm" />
                    </div>
                    <input placeholder="Phone *" value={newCustomer.phone}
                      onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                      className="w-full bg-background border border-slate-700 text-white p-2.5 rounded-lg focus:border-primary focus:outline-none text-sm" required />
                    <input placeholder="Email (optional)" value={newCustomer.email}
                      onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                      className="w-full bg-background border border-slate-700 text-white p-2.5 rounded-lg focus:border-primary focus:outline-none text-sm" />
                    <button type="button" onClick={async () => {
                      if (!newCustomer.first_name || !newCustomer.phone) { alert('First name and phone are required'); return; }
                      try {
                        const res = await axios.post('/api/customers', newCustomer);
                        if (res.data.success) {
                          const created = res.data.customer;
                          setCustomers(prev => [...prev, created]);
                          setNewBooking(prev => ({...prev, contact_id: String(created.id)}));
                          setAddingNewCustomer(false);
                          setNewCustomer({ first_name: '', last_name: '', phone: '', email: '' });
                        }
                      } catch (err) { alert('Error: ' + (err.response?.data?.error || err.message)); }
                    }}
                      className="w-full bg-primary hover:bg-purple-600 text-white p-2.5 rounded-lg font-semibold text-sm transition-colors">
                      ✓ Save & Select Customer
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-slate-300 mb-2 font-semibold">Service *</label>
                <select value={newBooking.service_id}
                  onChange={(e) => setNewBooking({...newBooking, service_id: e.target.value})}
                  className="w-full bg-background border border-slate-700 text-white p-3 rounded-lg focus:border-primary focus:outline-none" required>
                  <option value="">Select service...</option>
                  {services.map(s => (
                    <option key={s.id} value={s.id}>{s.name} - ${s.price} ({s.duration} min)</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-300 mb-2 font-semibold">Staff Member *</label>
                <select value={newBooking.staff_id}
                  onChange={(e) => setNewBooking({...newBooking, staff_id: e.target.value})}
                  className="w-full bg-background border border-slate-700 text-white p-3 rounded-lg focus:border-primary focus:outline-none" required>
                  <option value="">Select staff...</option>
                  {staff.map(s => (
                    <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                  ))}
                </select>
              </div>

              {/* DATE GRID — next 14 days */}
              <div>
                <label className="block text-slate-300 mb-2 font-semibold">Date *</label>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {[...Array(14)].map((_, i) => {
                    const d = addDays(new Date(), i);
                    const dStr = format(d, 'yyyy-MM-dd');
                    const isSelected = newBooking.booking_date === dStr;
                    return (
                      <button key={i} type="button"
                        onClick={() => setNewBooking({...newBooking, booking_date: dStr})}
                        className={`flex-shrink-0 text-center px-3 py-2 rounded-lg border transition-all min-w-[60px] ${
                          isSelected
                            ? 'bg-primary border-primary text-white shadow-lg'
                            : i === 0
                              ? 'bg-surface border-emerald-600 text-emerald-300 hover:border-primary'
                              : 'bg-surface border-slate-700 text-slate-300 hover:border-primary'
                        }`}>
                        <div className="text-xs">{i === 0 ? 'Today' : i === 1 ? 'Tmrw' : format(d, 'EEE')}</div>
                        <div className="text-lg font-bold">{format(d, 'd')}</div>
                        <div className="text-xs text-slate-400">{format(d, 'MMM')}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* TIME SLOT GRID */}
              <div>
                <label className="block text-slate-300 mb-2 font-semibold">Time *</label>
                {(() => {
                  const selectedDateStr = newBooking.booking_date;
                  const todayStr = format(new Date(), 'yyyy-MM-dd');
                  const nowHour = new Date().getHours();
                  const nowMin = new Date().getMinutes();

                  // Get booked times for selected staff + date
                  const staffId = newBooking.staff_id ? parseInt(newBooking.staff_id) : null;
                  const bookedSlots = staffId ? bookings.filter(b =>
                    (b.booking_date || '').slice(0, 10) === selectedDateStr &&
                    b.staff_id === staffId &&
                    ['confirmed', 'completed', 'pending'].includes(b.status)
                  ) : [];

                  // Generate slots 9:00 AM to 12:30 AM (next day)
                  const slots = [];
                  for (let h = 9; h <= 24; h++) {
                    for (let m = 0; m < 60; m += 30) {
                      if (h === 24 && m > 30) break;
                      const hour = h === 24 ? 0 : h;
                      const timeStr = `${String(hour).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                      const displayH = h > 12 ? h - 12 : h === 0 || h === 24 ? 12 : h;
                      const ampm = h >= 12 && h < 24 ? 'PM' : 'AM';
                      const display = `${displayH}:${String(m).padStart(2, '0')} ${ampm}`;

                      // Check if past
                      const isPast = selectedDateStr === todayStr && (h < nowHour || (h === nowHour && m <= nowMin));

                      // Check if booked
                      const isBooked = bookedSlots.some(b =>
                        timeStr >= b.start_time && timeStr < b.end_time
                      );

                      slots.push({ timeStr, display, isPast, isBooked });
                    }
                  }

                  return (
                    <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto pr-1">
                      {slots.map(slot => {
                        const isSelected = newBooking.start_time === slot.timeStr;
                        const disabled = slot.isPast || slot.isBooked;
                        return (
                          <button key={slot.timeStr} type="button"
                            disabled={disabled}
                            onClick={() => !disabled && setNewBooking({...newBooking, start_time: slot.timeStr})}
                            className={`py-2 px-1 rounded-lg text-sm font-semibold transition-all border ${
                              disabled
                                ? 'bg-slate-800/50 border-slate-700/50 text-slate-600 cursor-not-allowed line-through'
                                : isSelected
                                  ? 'bg-primary border-primary text-white shadow-lg'
                                  : 'bg-surface border-slate-700 text-slate-300 hover:border-primary hover:text-white'
                            }`}>
                            {slot.display}
                            {slot.isBooked && <div className="text-xs text-red-400 font-normal">Booked</div>}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
                {!newBooking.staff_id && (
                  <p className="text-xs text-amber-400 mt-2">Select a staff member to see availability</p>
                )}
              </div>

              <div>
                <label className="block text-slate-300 mb-2 font-semibold">Notes (optional)</label>
                <textarea value={newBooking.notes}
                  onChange={(e) => setNewBooking({...newBooking, notes: e.target.value})}
                  className="w-full bg-background border border-slate-700 text-white p-3 rounded-lg h-24 focus:border-primary focus:outline-none"
                  placeholder="Any special requests or notes..." />
              </div>

              <div className="flex gap-4 pt-4">
                <button type="submit"
                  className="flex-1 bg-primary hover:bg-purple-600 text-white py-3 rounded-lg font-semibold shadow-lg hover:scale-105 transition-all">
                  ✅ Create Booking
                </button>
                <button type="button" onClick={() => setShowNewBooking(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-semibold transition-all">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  // ============================================================================
  // BOOKING CARD RENDERER (shared by week + month views)
  // ============================================================================
  function renderBookingCards(dayBookings) {
    if (dayBookings.length === 0) {
      return (
        <div className="bg-surface p-12 rounded-lg border border-slate-700 text-center">
          <div className="text-6xl mb-4">📅</div>
          <div className="text-slate-400 text-lg mb-2">No bookings for this day</div>
          <button onClick={() => setShowNewBooking(true)}
            className="mt-4 text-primary hover:text-purple-400 font-semibold">
            Create first booking →
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {dayBookings.map(booking => {
          const service = booking.services || services.find(s => s.id === booking.service_id);
          const staffMember = booking.staff || staff.find(s => s.id === booking.staff_id);
          const customer = booking.contacts || customers.find(c => c.id === booking.contact_id);

          return (
            <div key={booking.id}
              className="bg-surface p-6 rounded-lg border border-slate-700 hover:border-primary transition-all hover:shadow-lg">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-2">
                    <span className="text-2xl font-bold text-primary">{booking.start_time}</span>
                    <div>
                      <h3 className="text-white font-semibold text-lg">
                        {customer?.first_name} {customer?.last_name}
                      </h3>
                      <p className="text-slate-400 text-sm">
                        {service?.name} • {service?.duration} min
                      </p>
                    </div>
                  </div>
                  {staffMember && (
                    <div className="text-slate-400 text-sm font-semibold">
                      👤 {staffMember.first_name} {staffMember.last_name}
                    </div>
                  )}
                  {booking.notes && (
                    <div className="mt-2 text-slate-400 text-sm bg-background p-2 rounded">
                      📝 {booking.notes}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-emerald-500 font-bold text-xl mb-2">
                    ${booking.final_price || service?.price || '—'}
                  </div>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    booking.status === 'completed' ? 'bg-emerald-900 text-emerald-300' :
                    booking.status === 'confirmed' ? 'bg-blue-900 text-blue-300' :
                    booking.status === 'checked_in' ? 'bg-amber-900 text-amber-300' :
                    booking.status === 'no-show' ? 'bg-red-900 text-red-300' :
                    booking.status === 'cancelled' ? 'bg-slate-700 text-slate-400' :
                    booking.status === 'pending' ? 'bg-orange-900 text-orange-300' :
                    'bg-slate-700 text-slate-300'
                  }`}>
                    {booking.status === 'no-show' ? '⚠️ NO-SHOW' : booking.status === 'checked_in' ? '🏁 CHECKED IN' : booking.status === 'pending' ? '⏳ PENDING APPROVAL' : (booking.status || 'pending').toUpperCase()}
                  </span>
                </div>
              </div>

              {booking.status === 'pending' && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-orange-700">
                  <button onClick={() => handleApprove(booking.id)}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors">
                    ✓ Approve & Notify Customer
                  </button>
                  <button onClick={() => handleDecline(booking.id)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors">
                    ✕ Decline
                  </button>
                </div>
              )}

              {booking.status === 'confirmed' && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-slate-700">
                  <button onClick={() => handleCheckIn(booking.id)}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-2 px-4 rounded-lg text-sm font-semibold transition-colors">
                    🏁 Check In
                  </button>
                  <button onClick={() => handleComplete(booking.id)}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2 px-4 rounded-lg text-sm font-semibold transition-colors">
                    ✅ Complete
                  </button>
                  <button onClick={() => handleNoShow(booking.id)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg text-sm font-semibold transition-colors">
                    ⚠️ No-Show
                  </button>
                  <button onClick={() => handleCancelBooking(booking.id)}
                    className="flex-1 bg-slate-600 hover:bg-slate-700 text-white py-2 px-4 rounded-lg text-sm font-semibold transition-colors">
                    ❌ Cancel
                  </button>
                </div>
              )}

              {booking.status === 'checked_in' && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-amber-700">
                  <div className="flex-1 bg-amber-900/30 text-amber-300 py-2 px-4 rounded-lg text-sm text-center">
                    🏁 Checked In — Work Order Created
                  </div>
                  <button onClick={() => handleComplete(booking.id)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white py-2 px-4 rounded-lg text-sm font-semibold transition-colors">
                    ✅ Complete
                  </button>
                </div>
              )}

              {booking.status === 'no-show' && (
                <div className="mt-4 pt-4 border-t border-red-700">
                  <p className="text-red-400 text-sm font-semibold">⚠️ Customer did not show up</p>
                </div>
              )}

              {booking.status === 'cancelled' && (
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <p className="text-slate-500 text-sm">
                    Cancelled {booking.cancelled_at ? format(new Date(booking.cancelled_at), 'MMM d, yyyy h:mm a') : ''}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
}

export default BookingCalendar;
